/**
 * ModelStats Component
 * Displays model performance metrics and comparisons
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { ModelStats as ModelStatsType } from '../../services/llm-monitoring';

interface ModelStatsProps {
  modelStats: ModelStatsType | null;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const ModelStats: React.FC<ModelStatsProps> = ({ modelStats }) => {
  if (!modelStats) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No model statistics available
        </Typography>
      </Box>
    );
  }

  const models = Object.entries(modelStats);
  
  // Prepare data for charts
  const costData = models.map(([name, stats]) => ({
    name: name.split('/').pop(),
    cost: parseFloat(Number(stats.totalCost).toFixed(4)),
    calls: stats.calls
  }));

  const performanceData = models.map(([name, stats]) => ({
    name: name.split('/').pop(),
    avgLatency: parseInt(stats.avgLatency),
    successRate: parseFloat(stats.successRate)
  }));

  const tokenData = models.map(([name, stats]) => ({
    name: name.split('/').pop(),
    value: stats.totalTokens
  }));

  // Calculate totals
  const totals = models.reduce((acc, [_, stats]) => ({
    calls: acc.calls + stats.calls,
    tokens: acc.tokens + Number(stats.totalTokens),
    cost: acc.cost + Number(stats.totalCost),
    errors: acc.errors + (stats.errors || 0)
  }), { calls: 0, tokens: 0, cost: 0, errors: 0 });

  const getModelColor = (index: number) => COLORS[index % COLORS.length];

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Total Models
              </Typography>
              <Typography variant="h4">{models.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Total Calls
              </Typography>
              <Typography variant="h4">{totals.calls.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Total Tokens
              </Typography>
              <Typography variant="h4">
                {(totals.tokens / 1000000).toFixed(2)}M
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Total Cost
              </Typography>
              <Typography variant="h4">${totals.cost.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Cost by Model
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={costData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value: any) => `$${value}`} />
                <Legend />
                <Bar dataKey="cost" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Token Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={tokenData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${(entry.value / 1000).toFixed(0)}K`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {tokenData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={getModelColor(index)} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: any) => `${(value / 1000).toFixed(0)}K tokens`} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Detailed Table */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Model Performance Details
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Model</TableCell>
                <TableCell align="right">Calls</TableCell>
                <TableCell align="right">Tokens</TableCell>
                <TableCell align="right">Avg Latency</TableCell>
                <TableCell align="right">Success Rate</TableCell>
                <TableCell align="right">Total Cost</TableCell>
                <TableCell align="right">Cost/Call</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {models.map(([name, stats], index) => (
                <TableRow key={name} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: getModelColor(index)
                        }}
                      />
                      <Typography variant="body2">
                        {name.split('/').pop()}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">{stats.calls.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    {(stats.totalTokens / 1000).toFixed(1)}K
                  </TableCell>
                  <TableCell align="right">{stats.avgLatency}ms</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={parseFloat(stats.successRate)}
                        sx={{ flexGrow: 1, height: 6 }}
                        color={parseFloat(stats.successRate) > 95 ? 'success' : 'warning'}
                      />
                      <Typography variant="caption">
                        {stats.successRate}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">${Number(stats.totalCost).toFixed(4)}</TableCell>
                  <TableCell align="right">${stats.avgCostPerCall}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ModelStats;